import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Home, ShieldCheck, Clock, FileText, HelpCircle, DollarSign, AlertTriangle } from 'lucide-react';

const brandRed  = "#b22335";
const brandNavy = "#3D3B6D";

const faqStyles = {
  section: {
    backgroundColor: 'white',
    padding: '30px 0',
  },
  subtitleWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    color: '#2b4d8d',
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: '15px',
    padding: '8px 25px',
    borderRadius: '50px',
    letterSpacing: '0.3px',
    boxShadow: '0 0 20px rgba(178, 35, 53, 0.25)',
    border: '1px solid rgba(178, 35, 53, 0.1)',
  },
  dot: {
    height: '8px',
    width: '8px',
    backgroundColor: brandRed,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '12px',
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'hauora-medium', sans-serif",
    color: brandNavy,
  },
  headingRed: {
    color: brandRed,
  },
  questionText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: 'clamp(1rem, calc(1rem + 0.55vw), 1rem)',
    color: brandNavy,
  },
  answerText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: '15px',
    color: '#4d5663',
    padding: '0 24px 24px 64px',
  },
  btnBase: {
    width: '100%',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
  },
};

/* Responsive heading via CSS injection — avoids a separate file */
const ResponsiveStyle = () => (
  <style>{`
    
    .faq-section-wrap {
      background-color: white;
      padding: 30px 10px;
    }
    @media (max-width: 991px) {
      .faq-section-wrap { padding: 20px 10px; }
    }
    @media (max-width: 576px) {
      .faq-section-wrap { padding: 20px 10px; }
      .faq-answer-text  { padding: 0 16px 20px 16px !important; font-size: 15px !important; }
      .faq-question-btn { padding: 16px !important; }
      .faq-subtitle     { font-size: 13px !important; padding: 7px 18px !important; }
    }
  `}</style>
);

const Faq = () => {
  const [activeId, setActiveId] = useState(null);

  const faqs = [
    {
      id: 1,
      icon: <Home size={20} />,
      question: "Do you provide free roof inspections?",
      answer: (
        <>
          Yes, we provide free roof inspections for homeowners and business owners in <a href="https://en.wikipedia.org/wiki/Powell,_Ohio" target="_blank" rel="noopener noreferrer" style={{ color: brandRed, textDecoration: 'none', fontWeight: '500' }}>Powell, Ohio</a>. Our team carefully evaluates your roof for leaks, storm damage, missing shingles, and structural issues. After inspection, we provide a clear report and honest recommendations. As a trusted Roofing contractor Powell, we ensure transparency and no-pressure service. Whether you are dealing with minor issues or major roofing concerns, our inspection helps you understand the exact condition of your roof. This service is part of our commitment as a leading Powell Roofing Company and Affordable roofing company, Powell, Ohio, helping customers make informed decisions.
        </>
      )
    },
    {
      id: 2,
      icon: <ShieldCheck size={20} />,
      question: "How do I find the best roofing contractor near me in Powell, Ohio?",
      answer: "To find the Best roofing company, Powell, Ohio, look for licensed, insured, and experienced contractors with strong customer reviews. A reliable contractor should offer free inspections, transparent pricing, and quality materials. Our company stands among the Best roofing company in Columbus, Ohio and surrounding areas, known for honest service and expert craftsmanship. Always check experience, warranties, and project portfolio before choosing. We are a trusted Residential roofing company, Powell, Ohio offering complete roofing solutions for homes and businesses."
    },
    {
      id: 3,
      icon: <HelpCircle size={20} />,
      question: "What roofing materials are best for Powell, Ohio weather?",
      answer: "The best roofing materials for Powell weather include asphalt shingles, metal roofing, and rubber roofing systems. These materials handle wind, rain, snow, and temperature changes effectively. Asphalt shingles are cost-effective, while metal roofing offers long-term durability. As a trusted Roofing Powell, Ohio expert, we recommend materials based on your budget and property type."
    },
    {
      id: 4,
      icon: <AlertTriangle size={20} />,
      question: "How do I know if I need a roof repair or replacement?",
      answer: "If your roof has leaks, missing shingles, sagging areas, or frequent repairs, it may need replacement. Minor issues can often be fixed with roof repair. Our Roof repair, Powell, Ohio experts inspect your roof and recommend the best solution based on condition and age."
    },
    {
      id: 5,
      icon: <Clock size={20} />,
      question: "How long does a roof installation take?",
      answer: "Most roof installations take 1–3 days, depending on size and weather conditions. Larger commercial projects may take longer. Our Roofing contractor, the Powell team, ensures fast, safe, and efficient installation."
    },
    {
      id: 6,
      icon: <DollarSign size={20} />,
      question: "How much does roof replacement cost in Powell, Ohio?",
      answer: "Roof replacement cost depends on material type, roof size, and complexity. On average, prices vary, but we offer affordable pricing as an affordable roofing company, Powell, Ohio, with flexible options for homeowners and businesses."
    },
    {
      id: 7,
      icon: <FileText size={20} />,
      question: "Do you offer emergency roof repair near me?",
      answer: "Yes, we provide 24/7 emergency roof repair services in Powell, Ohio. Whether it is storm damage, leaks, or sudden roofing issues, our team responds quickly to protect your property."
    }
  ];

  return (
    <>
      <ResponsiveStyle />
      
      <div className="faq-section-wrap cs-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>

              {/* Section Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {/* Subtitle pill */}
                <div className="cs-badge">
                  <span className="faq-subtitle" style={faqStyles.subtitleWrap}>
                    <span style={faqStyles.dot}></span>
                    Powell, Ohio Roofing Contractors FAQ
                  </span>
                </div>

                {/* Main heading */}
                <h2 className="cs-title cs-title-to-content" style={faqStyles.heading}>
                  Frequently Asked <span style={faqStyles.headingRed}>Questions.</span>
                </h2>
              </motion.div>

              {/* FAQ Items */}
              <div className="cs-content">
                {faqs.map((faq) => {
                  const isOpen = activeId === faq.id;
                  return (
                    <motion.div
                      key={faq.id}
                      initial={false}
                      animate={{ backgroundColor: isOpen ? '#fff' : 'rgba(255,255,255,0.6)' }}
                      className="mb-4 rounded-4 shadow-sm overflow-hidden"
                      style={{
                        border: '1px solid #eee',
                        borderLeft: `5px solid ${isOpen ? brandRed : brandNavy}`,
                        transition: 'border-left 0.3s ease',
                      }}
                    >
                      {/* Question Button */}
                      <button
                        className="faq-question-btn"
                        onClick={() => setActiveId(isOpen ? null : faq.id)}
                        style={faqStyles.btnBase}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span style={{ color: isOpen ? brandRed : brandNavy, flexShrink: 0 }}>
                            {faq.icon}
                          </span>
                          <span style={faqStyles.questionText}>
                            {faq.question}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ flexShrink: 0, marginLeft: '12px' }}
                        >
                          <ChevronDown color={isOpen ? brandRed : '#ccc'} />
                        </motion.div>
                      </button>

                      {/* Answer */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div
                              className="faq-answer-text"
                              style={faqStyles.answerText}
                            >
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Faq;